/*global WebView*/
/**
 * Contextual help : guided tour of a help section (roadmap lot L1.7f, spec
 * docs/superpowers/specs/2026-10-09-mise-en-evidence-spike.md § 4 — home-made, no library).
 *
 * A "Guided tour" button heads every section of the panel that has at least MIN_STEPS rows whose
 * zone is on the screen (rows of helpHighlight.js without the help-hl-missing class). The tour
 * shows these rows one by one :
 *   - the zone outlined by helpHighlight (same boxes, same scrolling, same scope) ;
 *   - a spotlight : the screen dimmed around the zone(s), under the panel (which stays readable) ;
 *   - a bubble beside the zone with the text of the row, Previous / Next / Close ;
 *   - keyboard : arrows (and Home / End), Escape ends the tour without closing the panel ;
 *   - it ends on screen change, on new panel content, when the panel closes, when its zone is
 *     gone, and on the last "Done".
 * No `<!-- tour -->` marker in the documentation : a section with two visible zones is enough
 * of a tour, and the button follows the screen (record, rights, tab) by itself.
 *
 * The pure functions (exported for jest) decide ; the singleton only touches the DOM.
 */
const helpHighlight = require("./helpHighlight") ;

const MIN_STEPS = 2 ;
const BUBBLE_GAP = 12 ;
const AREA_MARGIN = 8 ;
const SECTION = "[data-help-section]" ;
const ROW = "[data-help-highlight]" ;

//──────────────────────────── pure functions ────────────────────────────

/**
 * Rows that make the steps of a tour : those whose zone is on the screen
 *
 * @param {boolean[]} missing for each row of the section, in order : is its zone absent ?
 * @return {number[]} indexes of the step rows ; empty when there are fewer than MIN_STEPS
 */
function selectSteps(missing){
    const steps = [] ;
    (missing || []).forEach((m, i) => { if(!m){ steps.push(i) ; } }) ;
    return steps.length >= MIN_STEPS ? steps : [] ;
}

/**
 * Next step for a navigation command
 *
 * @param {number} index current step
 * @param {number} count number of steps
 * @param {string} action "next" | "prev" | "first" | "last" | "close"
 * @return {number} the new step, or -1 when the tour ends ("close", "next" on the last step)
 */
function navigate(index, count, action){
    if(!count || action === "close"){ return -1 ; }
    if(action === "next"){ return index+1 < count ? index+1 : -1 ; }
    if(action === "prev"){ return Math.max(0, index-1) ; }
    if(action === "first"){ return 0 ; }
    if(action === "last"){ return count-1 ; }
    return index ;
}

/**
 * Navigation command of a key while a tour runs, null for the keys it leaves alone
 *
 * @param {string} key KeyboardEvent.key
 * @return {string|null}
 */
function keyAction(key){
    switch(key){
        case "ArrowRight": case "ArrowDown": case "Right": case "Down": return "next" ;
        case "ArrowLeft": case "ArrowUp": case "Left": case "Up": return "prev" ;
        case "Home": return "first" ;
        case "End": return "last" ;
        case "Escape": case "Esc": return "close" ;
        default: return null ;
    }
}

/**
 * Bubble position beside the zone : below, above, right or left, the first side where it fits
 * in the visible area ; otherwise below, kept inside the area
 *
 * @param {object} zone {left, top, right, bottom} of the outlined zone (viewport coordinates)
 * @param {object} size {width, height} of the bubble
 * @param {object} area {left, top, right, bottom} visible part of the screen (under the top bar,
 *  left of the panel)
 * @param {number} [gap] space between the zone and the bubble
 * @return {object} {left, top, side}
 */
function placeBubble(zone, size, area, gap){
    const g = gap === undefined ? BUBBLE_GAP : gap ;
    const clampX = (x) => Math.max(area.left, Math.min(x, area.right - size.width)) ;
    const clampY = (y) => Math.max(area.top, Math.min(y, area.bottom - size.height)) ;
    const middleX = (zone.left + zone.right)/2 - size.width/2 ;
    const middleY = (zone.top + zone.bottom)/2 - size.height/2 ;
    const sides = [
        {side: "bottom", fits: zone.bottom + g + size.height <= area.bottom, left: clampX(middleX), top: zone.bottom + g},
        {side: "top", fits: zone.top - g - size.height >= area.top, left: clampX(middleX), top: zone.top - g - size.height},
        {side: "right", fits: zone.right + g + size.width <= area.right, left: zone.right + g, top: clampY(middleY)},
        {side: "left", fits: zone.left - g - size.width >= area.left, left: zone.left - g - size.width, top: clampY(middleY)},
    ] ;
    const chosen = sides.filter((s) => s.fits)[0] ;
    if(chosen){ return {left: Math.round(chosen.left), top: Math.round(chosen.top), side: chosen.side} ; }
    return {left: Math.round(clampX(middleX)), top: Math.round(clampY(zone.bottom + g)), side: "bottom"} ;
}

/**
 * Zones that overlap or touch, joined into their bounding box : with the even-odd rule, two
 * overlapping holes would dim their common part again
 *
 * @param {object[]} boxes {left, top, width, height}
 * @return {object[]} disjoint boxes, in the order of their first zone
 */
function mergeBoxes(boxes){
    const groups = (boxes || []).map((b) => ({left: b.left, top: b.top, right: b.left + b.width, bottom: b.top + b.height})) ;
    const meet = (a, b) => a.left <= b.right && b.left <= a.right && a.top <= b.bottom && b.top <= a.bottom ;
    for(let i = 0; i < groups.length; i++){
        for(let j = i+1; j < groups.length; j++){
            if(!meet(groups[i], groups[j])){ continue ; }
            const a = groups[i], b = groups[j] ;
            groups[i] = {left: Math.min(a.left, b.left), top: Math.min(a.top, b.top), right: Math.max(a.right, b.right), bottom: Math.max(a.bottom, b.bottom)} ;
            groups.splice(j, 1) ;
            j = i ;//the grown box may now meet a box already passed over
        }
    }
    return groups.map((g) => ({left: g.left, top: g.top, width: g.right - g.left, height: g.bottom - g.top})) ;
}

/**
 * CSS clip-path of the spotlight : the whole screen with a hole on each zone (even-odd rule)
 *
 * @param {object[]} boxes {left, top, width, height} of the outlined zones
 * @return {string} "polygon(evenodd, …)"
 */
function spotlightClip(boxes){
    const px = (n) => Math.round(n)+"px" ;
    const points = ["0 0", "100% 0", "100% 100%", "0 100%", "0 0"] ;
    mergeBoxes(boxes).forEach((b) => {
        const l = px(b.left), t = px(b.top), r = px(b.left + b.width), d = px(b.top + b.height) ;
        //each hole starts and ends where the outer path ended : no stray edge between holes
        points.push(l+" "+t, r+" "+t, r+" "+d, l+" "+d, l+" "+t, "0 0") ;
    }) ;
    return "polygon(evenodd, "+points.join(", ")+")" ;
}

/**
 * Bubble text of a row : title and description
 *
 * @param {string[]} cells text of the row cells (a table row) or [text] (a list item)
 * @param {string} fallbackTitle title when the row has no label cell (the section title)
 * @return {object} {title, text}
 */
function stepText(cells, fallbackTitle){
    const clean = (cells || []).map((c) => String(c || "").replace(/\s+/g, " ").trim()).filter((c) => c) ;
    if(clean.length > 1){ return {title: clean[0], text: clean.slice(1).join(" — ")} ; }
    return {title: fallbackTitle || "", text: clean[0] || ""} ;
}

//──────────────────────────── DOM singleton ────────────────────────────

const helpTour = {
    panel: null,
    tour: null, //{section, rows, index, overlay, bubble, starter}
} ;

function tr(key){
    try{
        return WebView.tr(key) ;
    }catch(err){
        return key ;
    }
}

function escapeHtml(text){
    return String(text === undefined || text === null ? "" : text)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;").replace(/'/g, "&#39;") ;
}

function reducedMotion(){
    return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches) ;
}

/**
 * Wire the tour to the panel. Idempotent.
 *
 * @param {object} panel helpPanel (host, appController)
 */
helpTour.attach = function(panel){
    if(this.panel){ return ; }
    this.panel = panel ;
    panel.host.addEventListener("click", (ev) => {
        const start = ev.target.closest("[data-help-tour-start]") ;
        if(start){
            ev.preventDefault() ;
            this.start(start.closest(SECTION), start) ;
        }
    }) ;
    //capture on window : before the panel (Escape closes it) and the Bootstrap modals (Escape
    //closes a popup screen)
    window.addEventListener("keydown", (ev) => this.onKey(ev), true) ;
    if(panel.appController && panel.appController.on){
        panel.appController.on("routeChanged", () => this.end()) ;
    }
    helpHighlight.onAvailability = (on) => this.refreshButtons(on) ;
} ;

/**
 * A "Guided tour" button on the sections with enough zones on the screen, none elsewhere
 *
 * @param {boolean} on the highlight is possible (panel open, not over the whole screen)
 */
helpTour.refreshButtons = function(on){
    if(!this.panel || !this.panel.host){ return ; }
    const sections = this.panel.host.querySelectorAll("[data-help-content] "+SECTION) ;
    for(let i=0; i<sections.length; i++){
        const section = sections[i] ;
        const rows = section.querySelectorAll(ROW) ;
        const missing = [] ;
        for(let r=0; r<rows.length; r++){ missing.push(rows[r].classList.contains("help-hl-missing")) ; }
        const show = on && selectSteps(missing).length > 0 ;
        let button = section.querySelector(":scope > [data-help-tour-start]") ;
        if(!show){
            if(button && !(this.tour && this.tour.section === section)){ button.parentNode.removeChild(button) ; }
            continue ;
        }
        if(!button){
            button = document.createElement("button") ;
            button.type = "button" ;
            button.className = "btn btn-sm btn-outline-primary help-tour-start" ;
            button.setAttribute("data-help-tour-start", "") ;
            button.innerHTML = '<i class="fas fa-route" aria-hidden="true"></i> <span>'+escapeHtml(tr("help.tour"))+'</span>' ;
            button.title = tr("help.tourHint") ;
            //after the section title, before its text
            const heading = section.firstElementChild && /^H[1-6]$/.test(section.firstElementChild.tagName) ? section.firstElementChild : null ;
            section.insertBefore(button, heading ? heading.nextSibling : section.firstChild) ;
        }
    }
} ;

/**
 * Start the tour of a section
 *
 * @param {Element} section [data-help-section] block of the panel
 * @param {Element} [starter] the button, focused again at the end
 */
helpTour.start = function(section, starter){
    this.end() ;
    if(!section || !helpHighlight.enabled()){ return ; }
    helpHighlight.refreshAvailability() ;//the screen may have changed since the last look
    const all = Array.prototype.slice.call(section.querySelectorAll(ROW)) ;
    const steps = selectSteps(all.map((r) => r.classList.contains("help-hl-missing"))).map((i) => all[i]) ;
    if(!steps.length){ return ; }

    const overlay = document.createElement("div") ;
    overlay.className = "help-tour-overlay"+(reducedMotion() ? " help-tour-still" : "") ;
    overlay.setAttribute("aria-hidden", "true") ;
    overlay.setAttribute("data-help-tour-overlay", "") ;
    const bubble = document.createElement("div") ;
    bubble.className = "help-tour-bubble"+(reducedMotion() ? " help-tour-still" : "") ;
    bubble.setAttribute("role", "dialog") ;
    bubble.setAttribute("aria-live", "polite") ;
    bubble.setAttribute("data-help-tour", "") ;
    bubble.addEventListener("click", (ev) => {
        const cmd = ev.target.closest("[data-help-tour-cmd]") ;
        if(cmd){ this.go(cmd.getAttribute("data-help-tour-cmd")) ; }
    }) ;
    document.body.appendChild(overlay) ;
    document.body.appendChild(bubble) ;

    const heading = section.querySelector("h1, h2, h3, h4") ;
    this.tour = {
        section: section,
        title: heading ? heading.textContent.trim() : "",
        rows: steps,
        index: -1,
        overlay: overlay,
        bubble: bubble,
        starter: starter || null,
    } ;
    section.classList.add("help-tour-running") ;
    helpHighlight.tour = this ;
    this.step(0, 1) ;
} ;

helpTour.isRunning = function(){
    return !!this.tour ;
} ;

/**
 * Navigation command (bubble buttons, keyboard)
 *
 * @param {string} action "next" | "prev" | "first" | "last" | "close"
 */
helpTour.go = function(action){
    if(!this.tour){ return ; }
    const to = navigate(this.tour.index, this.tour.rows.length, action) ;
    if(to === -1){ return this.end(true) ; }
    if(to !== this.tour.index){ this.step(to, to > this.tour.index ? 1 : -1) ; }
} ;

/**
 * Show a step ; a zone gone meanwhile (re-rendered view) is skipped in the same direction
 *
 * @param {number} index step to show
 * @param {number} direction 1 forward, -1 backward
 */
helpTour.step = function(index, direction){
    const tour = this.tour ;
    for(let i = index; i >= 0 && i < tour.rows.length; i += direction){
        const els = helpHighlight.show(tour.rows[i]) ;
        if(els.length){
            tour.index = i ;
            this.renderBubble() ;
            this.scrollRow(tour.rows[i]) ;
            this.redraw() ;
            const next = tour.bubble.querySelector('[data-help-tour-cmd="next"]') ;
            if(next){ next.focus({preventScroll: true}) ; }
            return ;
        }
    }
    //no zone left in that direction : stay on the current step, or end if there is none
    if(tour.index === -1 || !helpHighlight.show(tour.rows[tour.index]).length){ return this.end() ; }
    this.redraw() ;
} ;

/**
 * Keep the row of the step in view in the panel : the reader sees where the text comes from
 */
helpTour.scrollRow = function(row){
    const body = this.panel.host.querySelector("[data-help-body]") ;
    if(!body){ return ; }
    const r = row.getBoundingClientRect() ;
    const b = body.getBoundingClientRect() ;
    if(r.top < b.top || r.bottom > b.bottom){
        body.scrollTop += r.top - b.top - (b.height - r.height)/2 ;
    }
} ;

helpTour.renderBubble = function(){
    const tour = this.tour ;
    const row = tour.rows[tour.index] ;
    const cells = row.tagName === "TR"
        ? Array.prototype.map.call(row.cells, (c) => c.textContent)
        : [row.textContent] ;
    const text = stepText(cells, tour.title) ;
    const last = tour.index === tour.rows.length-1 ;
    const counter = tr("help.tourStep").replace("{n}", tour.index+1).replace("{total}", tour.rows.length) ;
    tour.bubble.setAttribute("aria-label", tr("help.tour")+(tour.title ? " — "+tour.title : "")) ;
    tour.bubble.innerHTML = ''
        +'<div class="help-tour-head">'
        +'<strong class="help-tour-title">'+escapeHtml(text.title)+'</strong>'
        +'<button type="button" class="close" data-help-tour-cmd="close" title="'+escapeHtml(tr("help.tourClose"))+'">'
        +'<span aria-hidden="true">&times;</span><span class="sr-only">'+escapeHtml(tr("help.tourClose"))+'</span></button>'
        +'</div>'
        +(text.text ? '<p class="help-tour-text">'+escapeHtml(text.text)+'</p>' : "")
        +'<div class="help-tour-nav">'
        +'<small class="help-tour-count text-muted" data-help-tour-count>'+escapeHtml(counter)+'</small>'
        +'<button type="button" class="btn btn-sm btn-light" data-help-tour-cmd="prev"'+(tour.index === 0 ? " disabled" : "")+'>'
        +'<i class="fas fa-chevron-left" aria-hidden="true"></i> '+escapeHtml(tr("help.tourPrev"))+'</button>'
        +'<button type="button" class="btn btn-sm btn-primary" data-help-tour-cmd="next">'
        +escapeHtml(tr(last ? "help.tourDone" : "help.tourNext"))
        +(last ? "" : ' <i class="fas fa-chevron-right" aria-hidden="true"></i>')+'</button>'
        +'</div>' ;
} ;

/**
 * Spotlight and bubble on the boxes drawn by helpHighlight (called again on every redraw :
 * scroll, resize, view re-rendered)
 */
helpTour.redraw = function(){
    const tour = this.tour ;
    if(!tour){ return ; }
    const boxes = helpHighlight.boxes.map((b) => b.getBoundingClientRect()) ;
    if(!boxes.length){ return ; }
    tour.overlay.style.clipPath = spotlightClip(boxes) ;
    tour.overlay.style.webkitClipPath = tour.overlay.style.clipPath ;
    const viewport = helpHighlight.viewport() ;
    const area = {
        left: AREA_MARGIN,
        top: viewport.top + AREA_MARGIN,
        right: viewport.right - AREA_MARGIN,
        bottom: viewport.height - AREA_MARGIN,
    } ;
    //beside the group of the first zone, not over the zones that touch it
    const first = mergeBoxes(boxes)[0] ;
    const size = {width: tour.bubble.offsetWidth, height: tour.bubble.offsetHeight} ;
    const pos = placeBubble({left: first.left, top: first.top, right: first.left + first.width, bottom: first.top + first.height}, size, area) ;
    tour.bubble.style.left = pos.left+"px" ;
    tour.bubble.style.top = pos.top+"px" ;
    tour.bubble.setAttribute("data-side", pos.side) ;
} ;

/**
 * The zone of the step is gone (redraw found nothing) : next step, or end
 */
helpTour.lost = function(){
    if(!this.tour){ return ; }
    const from = this.tour.index ;
    this.tour.index = -1 ;
    for(let i = from+1; i < this.tour.rows.length; i++){
        if(helpHighlight.show(this.tour.rows[i]).length){
            this.tour.index = i ;
            this.renderBubble() ;
            this.redraw() ;
            return ;
        }
    }
    this.end() ;
} ;

helpTour.onKey = function(ev){
    if(!this.tour){ return ; }
    const action = keyAction(ev.key) ;
    if(!action){ return ; }
    const target = ev.target ;
    const typing = target && (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) ;
    if(typing && action !== "close"){ return ; }
    ev.preventDefault() ;
    ev.stopPropagation() ;
    this.go(action) ;
} ;

/**
 * End the tour : nothing left on the screen
 *
 * @param {boolean} [focusStarter] give the focus back to the tour button (closed by the user)
 */
helpTour.end = function(focusStarter){
    const tour = this.tour ;
    if(!tour){ return ; }
    this.tour = null ;
    helpHighlight.tour = null ;
    helpHighlight.clear() ;
    //before removing the bubble : once removed, the focus it held falls back on the body
    const focusInBubble = tour.bubble.contains(document.activeElement) ;
    [tour.overlay, tour.bubble].forEach((el) => { if(el.parentNode){ el.parentNode.removeChild(el) ; } }) ;
    tour.section.classList.remove("help-tour-running") ;
    if(focusStarter === true || focusInBubble){
        if(tour.starter && document.body.contains(tour.starter)){ tour.starter.focus({preventScroll: true}) ; }
    }
} ;

helpTour.MIN_STEPS = MIN_STEPS ;
helpTour.selectSteps = selectSteps ;
helpTour.navigate = navigate ;
helpTour.keyAction = keyAction ;
helpTour.placeBubble = placeBubble ;
helpTour.spotlightClip = spotlightClip ;
helpTour.mergeBoxes = mergeBoxes ;
helpTour.stepText = stepText ;

module.exports = helpTour ;
